import React, { useState, useRef, useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useAcademy } from '../AcademyContext';
import ExerciseTopBar from '../components/ExerciseTopBar';
import ExerciseReward from '../components/ExerciseReward';
import { IconArrowRight, IconCheck, IconChevron } from '../components/Icons';

const countWords = (str) => (str || '').trim().split(/\s+/).filter(Boolean).length;

// ── SalesGod template parser ──
// Splits a template into typed segments so we can colorize and preview it:
//   {{ token }} → 'var'  (or 'greeting' for the reserved {{greeting}} snippet)
//   { prompt }  → 'prompt' (AI instruction, generated per contact at send time)
//   anything else → 'text'
const TEMPLATE_TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}|\{([^{}]+)\}/g;

const parseTemplate = (text) => {
  const segs = [];
  if (!text) return segs;
  let last = 0;
  let m;
  TEMPLATE_TOKEN_RE.lastIndex = 0;
  while ((m = TEMPLATE_TOKEN_RE.exec(text)) !== null) {
    if (m.index > last) segs.push({ type: 'text', value: text.slice(last, m.index) });
    if (m[1] !== undefined) {
      const token = m[1];
      segs.push({ type: token === 'greeting' ? 'greeting' : 'var', token, value: m[0] });
    } else {
      segs.push({ type: 'prompt', token: m[2].trim(), value: m[0] });
    }
    last = TEMPLATE_TOKEN_RE.lastIndex;
  }
  if (last < text.length) segs.push({ type: 'text', value: text.slice(last) });
  return segs;
};

/**
 * Practice Lab — copywriting / email-writing exercise.
 *
 * Modes (configured per lesson in the backend later):
 *  - 'easy'     → guided slots in a fixed psychological pattern, each filled in.
 *  - 'hard'     → only a brief; user writes subject + full email from scratch.
 *  - 'subject'  → user writes N subject lines only.
 *  - 'salesgod' → user writes a dynamic email TEMPLATE using {{greeting}},
 *                 {{db_variable}} (fixed DB values) and { ai prompt } (single
 *                 braces → generated per contact at send time).
 *
 * DUMMY: answers are local-only; submitting marks the practice as done.
 */

const CharCount = ({ value, limit }) => {
  const len = (value || '').length;
  const over = len > limit;
  return (
    <span className={`ac-pl-count ${over ? 'is-over' : ''}`}>
      {len}/{limit}
    </span>
  );
};

const PracticeLab = () => {
  const { topicId, courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { getTopic, getCourse, getLesson, markPracticeDone, getCourseSteps } = useAcademy();
  const topic = getTopic(topicId);
  const course = getCourse(topicId, courseId);
  const lesson = getLesson(topicId, courseId, lessonId);

  const lab = lesson && lesson.practiceLab ? lesson.practiceLab : null;

  // ── State (hooks must run before any early return) ──
  const [blocks, setBlocks] = useState(() =>
    lab && lab.mode === 'easy' ? lab.blocks.map((b) => ({ ...b, value: '' })) : []
  );
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [subjects, setSubjects] = useState(() =>
    lab && lab.mode === 'subject'
      ? Array.from({ length: (lab.angles && lab.angles.length) || lab.subjectCount || 3 }, () => '')
      : []
  );
  const [showStructure, setShowStructure] = useState(false);
  const [template, setTemplate] = useState(() =>
    lab && lab.mode === 'salesgod' ? lab.starter || '' : ''
  );
  // Destination to navigate to once the success reward has played.
  const [rewardTo, setRewardTo] = useState(null);
  const templateRef = useRef(null);

  // Each challenge starts at the top of the page, even if the previous step
  // was completed while scrolled down.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  if (!topic || !course || !lesson) return <Navigate to="/academy" replace />;
  if (!lab) return <Navigate to={`/academy/${topicId}/${courseId}`} replace />;

  const email = lab.email || {};

  // ── SalesGod (template) derived state ──
  const sgVariables = lab.variables || [];
  const sgVarMap = Object.fromEntries(sgVariables.map((v) => [v.token, v]));
  const sgSegs = lab.mode === 'salesgod' ? parseTemplate(template) : [];
  const sgHasGreeting = sgSegs.some((s) => s.type === 'greeting');
  const sgHasVar = sgSegs.some((s) => s.type === 'var');
  const sgHasPrompt = sgSegs.some((s) => s.type === 'prompt');
  const sgStray = sgSegs.some((s) => s.type === 'text' && /[{}]/.test(s.value));
  const sgVarCount = sgSegs.filter((s) => s.type === 'var' || s.type === 'greeting').length;
  const sgPromptCount = sgSegs.filter((s) => s.type === 'prompt').length;

  // Insert a snippet at the caret position in the template editor. The optional
  // caretBack moves the caret left from the end (used to land inside { }).
  const insertAtCursor = (snippet, caretBack = 0) => {
    const ta = templateRef.current;
    if (!ta) {
      setTemplate((t) => t + snippet);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = template.slice(0, start) + snippet + template.slice(end);
    setTemplate(next);
    const pos = start + snippet.length - caretBack;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };

  const sgChecks = [
    { id: 'greeting', label: 'Du startest mit {{greeting}}', done: sgHasGreeting },
    {
      id: 'var',
      label: 'Du baust mindestens eine Datenbank-Variable {{…}} ein',
      done: sgHasVar,
    },
    {
      id: 'prompt',
      label: 'Du nutzt mindestens einen KI-Prompt { … }',
      done: sgHasPrompt,
    },
    {
      id: 'balanced',
      label: 'Alle Klammern sind sauber gesetzt',
      done: template.trim().length > 0 && !sgStray,
    },
  ];

  // Word limit is a hard cap: reject edits that grow a block past its limit.
  const setBlockValue = (id, value) =>
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== id) return b;
        if (b.wordLimit) {
          const next = countWords(value);
          const current = countWords(b.value);
          if (next > b.wordLimit && next > current) return b;
        }
        return { ...b, value };
      })
    );

  const findBlock = (id) => blocks.find((b) => b.id === id);

  const withinWordLimits = blocks.every(
    (b) => !b.wordLimit || countWords(b.value) <= b.wordLimit
  );

  // ── Self-check (easy mode) ──
  const buildChecks = () => {
    const items = [];
    items.push({
      id: 'subject',
      label: 'Du hast einen Betreff formuliert',
      done: subject.trim().length > 0,
    });
    items.push({
      id: 'filled',
      label: 'Jeder Abschnitt ist ausgefüllt',
      done: blocks.length > 0 && blocks.every((b) => b.value.trim()),
    });
    const ice = findBlock('icebreaker');
    if (ice) {
      items.push({
        id: 'opener',
        label: 'Dein Eisbrecher ist konkret, kein generisches „Hallo zusammen“',
        done: ice.value.trim().length >= 15,
      });
    }
    const proof = findBlock('proof');
    if (proof) {
      items.push({
        id: 'proof',
        label: 'Dein Beweis nennt eine konkrete Zahl',
        done: /\d/.test(proof.value),
      });
    }
    const cta = findBlock('cta');
    if (cta) {
      const len = cta.value.trim().length;
      items.push({
        id: 'cta',
        label: 'Dein CTA ist eine kurze, klare Handlungsaufforderung',
        done: len > 0 && len <= 160,
      });
    }
    items.push({
      id: 'words',
      label: 'Du bleibst in allen Wortlimits',
      done: blocks.length > 0 && withinWordLimits,
    });
    return items;
  };

  const setSubjectAt = (i, value) =>
    setSubjects((prev) => prev.map((s, idx) => (idx === i ? value : s)));

  const canSubmit =
    lab.mode === 'easy'
      ? blocks.every((b) => b.value.trim()) && withinWordLimits && subject.trim()
      : lab.mode === 'hard'
      ? subject.trim() &&
        body.trim() &&
        (lab.wordLimit ? countWords(body) <= lab.wordLimit : lab.charLimit ? body.length <= lab.charLimit : true)
      : lab.mode === 'salesgod'
      ? sgHasGreeting && sgHasVar && sgHasPrompt && !sgStray
      : subjects.every((s) => s.trim() && s.length <= lab.charLimit);

  // After submitting, jump straight to the next (now unlocked) step instead of
  // showing a confirmation screen.
  const stepRoute = (step) => {
    const base = `/academy/${topicId}/${courseId}/${step.lesson.id}`;
    if (step.type === 'practice') return `${base}/practice`;
    if (step.type === 'quiz') return `${base}/quiz`;
    return base; // video / special task live on the lesson page
  };

  const buildSubmission = () => {
    if (lab.mode === 'easy') {
      return {
        mode: 'easy',
        subject,
        blocks: blocks.map((b) => ({ label: b.label || b.title || b.id, value: b.value || '' })),
      };
    }
    if (lab.mode === 'hard') return { mode: 'hard', subject, body };
    if (lab.mode === 'subject') return { mode: 'subject', subjects };
    if (lab.mode === 'salesgod') return { mode: 'salesgod', template };
    return { mode: lab.mode || 'unknown' };
  };

  const handleSubmit = () => {
    if (rewardTo) return;
    markPracticeDone(topicId, courseId, lessonId, buildSubmission());
    const steps = getCourseSteps(topicId, course);
    const idx = steps.findIndex((s) => s.type === 'practice' && s.lesson.id === lessonId);
    const nextStep = idx >= 0 ? steps[idx + 1] : null;
    setRewardTo(nextStep ? stepRoute(nextStep) : `/academy/${topicId}/${courseId}`);
  };

  const closeTo = `/academy/${topicId}/${courseId}`;

  return (
    <main className="ac-page ac-pl-page">
      <ExerciseTopBar kicker={course.title} title={lesson.title} closeTo={closeTo} />

      <div className="ac-pl-head">
        <span className="ac-pl-badge">
          <img
            src="/img/salesgod_icon.svg"
            alt=""
            aria-hidden="true"
            className="ac-pl-badge-icon"
          />
          <span className="ac-pl-badge-text">Practice Lab</span>
        </span>
      </div>

      <h1 className="ac-pl-title">{lab.title}</h1>

      <div className="ac-pl-layout">
        {/* ── Left: the writing area ── */}
        <div className="ac-pl-main">
          {lab.mode === 'easy' && (
            <>
              <div className="ac-pl-mail">
                <div className="ac-pl-mail-bar">
                  <span className="ac-pl-mail-bar-title">Neue Nachricht</span>
                  <span className="ac-pl-mail-ctrls" aria-hidden="true">
                    <i className="ac-pl-mc ac-pl-mc-min" />
                    <i className="ac-pl-mc ac-pl-mc-max" />
                    <i className="ac-pl-mc ac-pl-mc-close" />
                  </span>
                </div>

                <div className="ac-pl-mail-fields">
                  {email.toName && (
                    <div className="ac-pl-mail-row">
                      <span className="ac-pl-mail-key">An</span>
                      <span className="ac-pl-mail-val">
                        {email.toName}
                        {email.to ? ` <${email.to}>` : ''}
                      </span>
                    </div>
                  )}
                  <div className="ac-pl-mail-row">
                    <span className="ac-pl-mail-key">Betreff</span>
                    <input
                      className="ac-pl-mail-subject"
                      placeholder={email.subjectPlaceholder || 'Betreff formulieren…'}
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </div>
                </div>

                <div className="ac-pl-mail-body">
                  {email.greeting && <p className="ac-pl-mail-static">{email.greeting}</p>}

                  {blocks.map((block, i) => {
                    const words = countWords(block.value);
                    const full = block.wordLimit && words >= block.wordLimit;
                    return (
                      <div className="ac-pl-slot" key={block.id}>
                        <div className="ac-pl-slot-head">
                          <span className="ac-pl-slot-no">{i + 1}</span>
                          <span className="ac-pl-slot-label">{block.label}</span>
                          <span className="ac-pl-slot-hint">{block.hint}</span>
                        </div>
                        <textarea
                          className="ac-pl-slot-input"
                          placeholder={block.placeholder || `${block.label} eintragen…`}
                          value={block.value}
                          onChange={(e) => setBlockValue(block.id, e.target.value)}
                        />
                        {block.wordLimit && (
                          <div className="ac-pl-slot-foot">
                            <span className={`ac-pl-words ${full ? 'is-full' : ''}`}>
                              {words}/{block.wordLimit} Wörter
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {email.signoff && (
                    <p className="ac-pl-mail-static ac-pl-mail-signoff">{email.signoff}</p>
                  )}
                </div>
              </div>

              <div className="ac-pl-check">
                <span className="ac-pl-brief-label">Selbst-Check</span>
                <ul className="ac-pl-check-list">
                  {buildChecks().map((item) => (
                    <li
                      key={item.id}
                      className={`ac-pl-check-item ${item.done ? 'is-done' : ''}`}
                    >
                      <span className="ac-pl-check-mark">
                        {item.done && <IconCheck size={12} />}
                      </span>
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {lab.mode === 'hard' && (
            <div className="ac-pl-form">
              <div className="ac-field">
                <label className="ac-field-label">Betreff</label>
                <input
                  className="ac-input"
                  placeholder="Betreffzeile…"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div className="ac-field">
                <div className="ac-pl-field-top">
                  <label className="ac-field-label">E-Mail-Text</label>
                  {lab.wordLimit ? (
                    <span
                      className={`ac-pl-count ${
                        countWords(body) > lab.wordLimit ? 'is-over' : ''
                      }`}
                    >
                      {countWords(body)}/{lab.wordLimit} Wörter
                    </span>
                  ) : (
                    <CharCount value={body} limit={lab.charLimit} />
                  )}
                </div>
                <textarea
                  className="ac-textarea"
                  style={{ minHeight: 280 }}
                  placeholder="Schreibe die komplette E-Mail…"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                />
              </div>

              {lab.structure && lab.structure.length > 0 && (
                <div className={`ac-pl-structure ${showStructure ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="ac-pl-structure-toggle"
                    onClick={() => setShowStructure((v) => !v)}
                    aria-expanded={showStructure}
                  >
                    <span className="ac-pl-structure-flow">
                      {lab.structure.map((s, i) => (
                        <React.Fragment key={s.label}>
                          {i > 0 && <span className="ac-pl-structure-sep">›</span>}
                          <span className="ac-pl-structure-chip">{s.label}</span>
                        </React.Fragment>
                      ))}
                    </span>
                    <span className="ac-pl-structure-chevron">
                      <IconChevron size={16} />
                    </span>
                  </button>

                  {showStructure && (() => {
                    // Each section now carries an explicit word count (like easy mode).
                    // Fall back to the legacy weight model for older content.
                    const sectionWords = (s) => {
                      if (s.words != null && s.words !== '') return Number(s.words);
                      if (lab.wordLimit && s.weight) return Math.round(s.weight * lab.wordLimit);
                      return null;
                    };
                    const totalWords = lab.structure.reduce((n, s) => n + (sectionWords(s) || 0), 0);
                    return (
                    <div className="ac-pl-structure-detail">
                      {totalWords > 0 && (
                        <span className="ac-pl-structure-limit">
                          Richtwert: ca. {totalWords} Wörter gesamt.
                        </span>
                      )}
                      <ol className="ac-pl-structure-list">
                        {lab.structure.map((s, i) => {
                          const words = sectionWords(s);
                          const pct = totalWords
                            ? Math.round(((words || 0) / totalWords) * 100)
                            : Math.round((s.weight || 1 / lab.structure.length) * 100);
                          return (
                            <li
                              className="ac-pl-structure-item"
                              key={s.label}
                              style={{ flexGrow: pct }}
                            >
                              <span className="ac-pl-structure-no">{i + 1}</span>
                              <span className="ac-pl-structure-text">
                                <span className="ac-pl-structure-row">
                                  <span className="ac-pl-structure-label">{s.label}</span>
                                  <span className="ac-pl-structure-weight">
                                    {words ? `ca. ${words} Wörter · ` : ''}
                                    {pct}%
                                  </span>
                                </span>
                                <span className="ac-pl-structure-hint">{s.hint}</span>
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {lab.mode === 'subject' && (
            <div className="ac-pl-form">
              {subjects.map((value, i) => {
                const angle = lab.angles ? lab.angles[i] : null;
                return (
                  <div className="ac-field" key={i}>
                    <div className="ac-pl-field-top">
                      <label className="ac-field-label">Betreffzeile {i + 1}</label>
                      <CharCount value={value} limit={lab.charLimit} />
                    </div>
                    {angle && (
                      <div className="ac-pl-angle">
                        <span className="ac-pl-angle-name">{angle.name}</span>
                        <span className="ac-pl-angle-hint">{angle.hint}</span>
                        {angle.example && (
                          <span className="ac-pl-angle-example">z. B. „{angle.example}“</span>
                        )}
                      </div>
                    )}
                    <input
                      className="ac-input"
                      placeholder={angle ? `${angle.name}-Betreffzeile…` : `Betreffzeile ${i + 1}…`}
                      value={value}
                      onChange={(e) => setSubjectAt(i, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {lab.mode === 'salesgod' && (
            <div className="ac-pl-sg">
              <div className="ac-pl-sg-legend">
                <div className="ac-pl-sg-leg">
                  <code className="ac-pl-tok is-greeting">{'{{greeting}}'}</code>
                  <span>Dynamische Begrüßung, automatisch personalisiert.</span>
                </div>
                <div className="ac-pl-sg-leg">
                  <code className="ac-pl-tok is-var">{'{{variable}}'}</code>
                  <span>Feste Variable aus der Datenbank, immer gleich ersetzt.</span>
                </div>
                <div className="ac-pl-sg-leg">
                  <code className="ac-pl-tok is-prompt">{'{ KI-Prompt }'}</code>
                  <span>Einfache Klammern, beim Versand pro Kontakt generiert.</span>
                </div>
              </div>

              <div className="ac-pl-sg-tools">
                <span className="ac-pl-sg-tools-label">Variablen einfügen</span>
                <div className="ac-pl-sg-chips">
                  {sgVariables.map((v) => (
                    <button
                      key={v.token}
                      type="button"
                      className={`ac-pl-sg-chip ${v.special ? 'is-greeting' : 'is-var'}`}
                      onClick={() => insertAtCursor(`{{${v.token}}}`)}
                      title={v.label}
                    >
                      {`{{${v.token}}}`}
                    </button>
                  ))}
                </div>
              </div>

              {lab.promptExamples && lab.promptExamples.length > 0 && (
                <div className="ac-pl-sg-tools">
                  <span className="ac-pl-sg-tools-label">KI-Prompt-Ideen</span>
                  <div className="ac-pl-sg-chips">
                    {lab.promptExamples.map((ex, i) => (
                      <button
                        key={i}
                        type="button"
                        className="ac-pl-sg-chip is-prompt"
                        onClick={() => insertAtCursor(`{ ${ex} }`)}
                      >
                        {`{ ${ex} }`}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="ac-pl-sg-chip is-prompt is-empty"
                      onClick={() => insertAtCursor('{  }', 2)}
                    >
                      + Eigener Prompt
                    </button>
                  </div>
                </div>
              )}

              <div className="ac-field">
                <label className="ac-field-label">E-Mail-Template</label>
                <textarea
                  ref={templateRef}
                  className="ac-textarea ac-pl-sg-editor"
                  style={{ minHeight: 200 }}
                  placeholder={'{{greeting}}\n\nich habe gesehen, dass { konkreter Bezug }…'}
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                />
              </div>

              <div className="ac-pl-sg-views">
                <div className="ac-pl-sg-view">
                  <span className="ac-pl-brief-label">Erkannte Bausteine</span>
                  <div className="ac-pl-sg-render">
                    {sgSegs.length === 0 ? (
                      <span className="ac-pl-sg-empty">Noch nichts geschrieben…</span>
                    ) : (
                      sgSegs.map((s, i) =>
                        s.type === 'text' ? (
                          <span key={i}>{s.value}</span>
                        ) : (
                          <span key={i} className={`ac-pl-tok is-${s.type}`}>
                            {s.value}
                          </span>
                        )
                      )
                    )}
                  </div>
                </div>

                <div className="ac-pl-sg-view">
                  <span className="ac-pl-brief-label">Vorschau (Beispielkontakt)</span>
                  <div className="ac-pl-sg-render">
                    {sgSegs.length === 0 ? (
                      <span className="ac-pl-sg-empty">Noch nichts geschrieben…</span>
                    ) : (
                      sgSegs.map((s, i) => {
                        if (s.type === 'text') return <span key={i}>{s.value}</span>;
                        if (s.type === 'prompt')
                          return (
                            <span key={i} className="ac-pl-tok is-prompt-out">
                              KI: {s.token}
                            </span>
                          );
                        const v = sgVarMap[s.token];
                        return (
                          <span key={i} className="ac-pl-sg-filled">
                            {v ? v.sample : s.value}
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              <div className="ac-pl-check">
                <span className="ac-pl-brief-label">Selbst-Check</span>
                <ul className="ac-pl-check-list">
                  {sgChecks.map((item) => (
                    <li
                      key={item.id}
                      className={`ac-pl-check-item ${item.done ? 'is-done' : ''}`}
                    >
                      <span className="ac-pl-check-mark">
                        {item.done && <IconCheck size={12} />}
                      </span>
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="ac-pl-foot">
            {lab.mode === 'easy' ? (
              <span className="ac-pl-count">
                {blocks.filter((b) => b.value.trim()).length}/{blocks.length} Abschnitte ausgefüllt
              </span>
            ) : lab.mode === 'salesgod' ? (
              <span className="ac-pl-count">
                {sgVarCount} Variablen · {sgPromptCount} KI-Prompts
              </span>
            ) : (
              <span />
            )}
            <button
              type="button"
              className="ac-btn ac-btn-primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              Übung abgeben <IconArrowRight size={15} />
            </button>
          </div>
        </div>

        {/* ── Right: brief + context ── */}
        <aside className="ac-pl-aside">
          {email.note && <div className="ac-pl-aside-note">{email.note}</div>}

          <div className="ac-pl-brief">
            <span className="ac-pl-brief-label">Briefing</span>
            <p>{lab.brief}</p>
          </div>

          {lab.context && (
            <div className="ac-pl-context">
              <span className="ac-pl-brief-label">Kontext zum Arbeiten</span>
              {lab.context.scenario && (
                <p className="ac-pl-context-scenario">{lab.context.scenario}</p>
              )}
              {lab.context.facts && lab.context.facts.length > 0 && (
                <div className="ac-pl-facts">
                  {lab.context.facts.map((fact) => (
                    <div className="ac-pl-fact" key={fact.label}>
                      <span className="ac-pl-fact-label">{fact.label}</span>
                      <span className="ac-pl-fact-value">{fact.value}</span>
                    </div>
                  ))}
                </div>
              )}
              {lab.context.notes && lab.context.notes.length > 0 && (
                <ul className="ac-pl-notes">
                  {lab.context.notes.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </aside>
      </div>

      {rewardTo && (
        <ExerciseReward
          onComplete={() => navigate(rewardTo, { state: { dir: 'forward' } })}
        />
      )}
    </main>
  );
};

export default PracticeLab;
